import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  ElementRef,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuPanel } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * One filter in the band: icon, "Navn · værdi", and either the dropdown arrow (at the page's
 * standard) or a tonal green with × (changed, R3/R4).
 *
 * The chip opens a `mat-menu` when given one (`[menu]`); otherwise it emits `opened` and exposes
 * {@link origin} for a CDK overlay (the period panel). It never shrinks; the value is cut at 200px
 * with the full text in a tooltip.
 *
 * Used directly for chips without a panel — "Udvalgte bookinger (n)" — and inside
 * `abook-filter-select` and `abook-period-chip`.
 */
@Component({
  selector: 'abook-filter-chip',
  imports: [NgTemplateOutlet, OverlayModule, MatIconModule, MatMenuModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'abook-filter-chip',
    '[class.is-changed]': 'changed()',
  },
  template: `
    <span
      class="chip"
      [class.chip--changed]="changed() && !showRequired()"
      [class.chip--required]="showRequired()"
      [class.chip--invalid]="invalid()"
      [class.chip--compact]="compact()"
      [class.chip--icon-only]="iconOnly()"
    >
      @if (menu(); as m) {
        <button
          type="button"
          class="chip__main"
          [matMenuTriggerFor]="m"
          [attr.aria-label]="ariaLabel()"
          [matTooltip]="ariaLabel()"
          [matTooltipDisabled]="!iconOnly()"
        >
          <ng-container *ngTemplateOutlet="body" />
        </button>
      } @else {
        <button
          type="button"
          class="chip__main"
          cdkOverlayOrigin
          [attr.aria-label]="ariaLabel()"
          [matTooltip]="ariaLabel()"
          [matTooltipDisabled]="!iconOnly()"
          [attr.aria-expanded]="expanded() ?? null"
          [attr.aria-haspopup]="hasPopup() ? 'dialog' : null"
          (click)="opened.emit()"
        >
          <ng-container *ngTemplateOutlet="body" />
        </button>
      }
      @if (showClear()) {
        <button
          type="button"
          class="chip__clear"
          [attr.aria-label]="'Nulstil ' + label()"
          (click)="cleared.emit()"
        >
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      }
    </span>

    <ng-template #body>
      <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
      @if (!iconOnly()) {
        @if (showRequired()) {
          <span class="chip__name">{{ requiredText() }}</span>
        } @else {
          <span class="chip__name">{{ label() }}</span>
          @if (value()) {
            <span class="chip__sep" aria-hidden="true">·</span>
            <span
              #valueEl
              class="chip__value"
              [matTooltip]="value()"
              [matTooltipDisabled]="!truncated()"
              (mouseenter)="measure()"
              (focusin)="measure()"
              >{{ value() }}</span
            >
          }
        }
      }
      @if (hasPopup() && !showClear() && !iconOnly()) {
        <mat-icon class="chip__arrow" aria-hidden="true">arrow_drop_down</mat-icon>
      }
    </ng-template>
  `,
  styleUrl: './abook-filter-chip.component.scss',
})
export class AbookFilterChipComponent {
  readonly icon = input.required<string>();
  readonly label = input.required<string>();
  /** The value text — "Arena Vejle", "Arena Vejle +2", "Alle". Empty shows the name alone. */
  readonly value = input<string>('');
  /** Differs from the page's standard: tonal green with × (R4). */
  readonly changed = input(false, { transform: booleanAttribute });
  /** Whether × is offered when changed. Off for a chip that cannot go back on its own. */
  readonly clearable = input(true, { transform: booleanAttribute });
  /** 36px instead of 40px. */
  readonly compact = input(false, { transform: booleanAttribute });
  /**
   * The icon alone, e.g. in a narrow page's corner (#1075): the text is the aria-label and a
   * tooltip, and a changed chip keeps its tonal green and ×.
   */
  readonly iconOnly = input(false, { transform: booleanAttribute });
  /** A filter the page cannot load without: dashed green border and {@link requiredText}. */
  readonly required = input(false, { transform: booleanAttribute });
  readonly requiredText = input('Vælg');
  /** Missing or wrong after a submit (the search variant, #880): red border; the page shows why. */
  readonly invalid = input(false, { transform: booleanAttribute });
  /** A mat-menu to open on click. Without one the chip emits {@link opened}. */
  readonly menu = input<MatMenuPanel | null>(null);
  /** Whether a click opens a panel — shows the arrow. Off for a plain chip like "Udvalgte bookinger". */
  readonly hasPopup = input(true, { transform: booleanAttribute });
  /** For a CDK-overlay panel, which has no trigger directive to set it. */
  readonly expanded = input<boolean | undefined>(undefined);

  readonly opened = output<void>();
  readonly cleared = output<void>();

  /** The anchor for a CDK overlay opened from {@link opened}. */
  readonly origin = viewChild(CdkOverlayOrigin);

  private readonly valueEl = viewChild<ElementRef<HTMLElement>>('valueEl');
  protected readonly truncated = signal(false);

  protected readonly showRequired = computed(() => this.required() && !this.value());
  /** × instead of the arrow: changed, clearable and not waiting for a required choice. */
  protected readonly showClear = computed(
    () => this.changed() && this.clearable() && !this.showRequired(),
  );
  protected readonly ariaLabel = computed(() =>
    this.showRequired()
      ? this.requiredText()
      : this.value()
        ? `${this.label()}: ${this.value()}`
        : this.label(),
  );

  protected measure(): void {
    const el = this.valueEl()?.nativeElement;
    this.truncated.set(!!el && el.scrollWidth > el.clientWidth);
  }
}
